import React from 'react';
import { Box, Card, Grid, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PeopleAlt, 
  DirectionsCar, 
  EmojiPeople, 
  Timeline,
  TrendingUp,
  Speed
} from '@mui/icons-material';
import CountUp from 'react-countup';

// Styled Components
const StatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)',
  borderRadius: 24,
}));

const StatCard = styled(Card)(({ gradient }) => ({
  position: 'relative',
  padding: '5px',
  minHeight: '180px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: gradient,
  borderRadius: 20,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
    '& .stat-icon': {
      transform: 'scale(1.1) rotate(10deg)',
    },
  },
}));

const IconContainer = styled(Box)({
  position: 'absolute',
  right: '-20px',
  top: '-20px',
  width: '120px',
  height: '120px',
  borderRadius: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.15,
  transition: 'transform 0.3s ease-in-out',
});

const StatValue = styled(Typography)({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  zIndex: 1,
});

const StatLabel = styled(Typography)({
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.9)',
  fontWeight: 500,
  zIndex: 1,
});

const TrendIndicator = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: 'rgba(255,255,255,0.9)',
  fontSize: '0.875rem',
});

export const GeneralStatistics = ({ dashboard }) => {
  const stats = [
    {
      title: 'Total Passengers',
      value: dashboard.passengers,
      icon: PeopleAlt,
      gradient: 'linear-gradient(135deg, #6B46C1 0%, #9F7AEA 100%)',
      trend: '+12.5%',
      subtitle: 'Active users this month',
    },
    {
      title: 'Registered Vehicles',
      value: dashboard.vehicles,
      icon: DirectionsCar,
      gradient: 'linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)',
      trend: '+8.2%',
      subtitle: 'Fleet growth rate',
    },
    {
      title: 'Active Drivers',
      value: dashboard.drivers,
      icon: EmojiPeople,
      gradient: 'linear-gradient(135deg, #4FD1C5 0%, #38B2AC 100%)',
      trend: '+15.7%',
      subtitle: 'Driver participation',
    },
    {
      title: 'Total Trips',
      value: dashboard.trips,
      icon: Timeline,
      gradient: 'linear-gradient(135deg, #F687B3 0%, #ED64A6 100%)',
      trend: '+23.1%',
      subtitle: 'Completed journeys',
    },
  ];

  return (
    <StatsContainer>
      <Box mb={4}>
        <Typography variant="h5" fontWeight={600} color="text.primary">
          General statistics
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
         
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard gradient={stat.gradient}>
              <IconContainer className="stat-icon">
                <stat.icon sx={{ fontSize: 80, color: 'white' }} />
              </IconContainer>

              <Box>
                <StatLabel>{stat.title}</StatLabel>
                <StatValue>
                  <CountUp 
                    end={stat.value} 
                    duration={2.5} 
                    separator="," 
                    decimals={0}
                  />
                </StatValue>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {stat.subtitle}
                  </Typography>
                  <TrendIndicator>
                    <TrendingUp sx={{ fontSize: 1 }} />
                    {stat.trend}
                  </TrendIndicator>
                </Box>
              </Box>

              {/* Decorative Speed Indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  opacity: 0.1,
                }}
              >
                <Speed sx={{ fontSize: 40 }} />
              </Box>
            </StatCard>
          </Grid>
        ))}
      </Grid>
    </StatsContainer>
  );
};

export default GeneralStatistics;